import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError, APIMessageResponse } from '@/shared/services/apiConfig';
import {
  FileUploadMeta,
  UploadCompleteRequest,
  UploadCompleteResponse,
  UploadFilesInput,
  UploadUrlFileResult,
  UploadUrlRequest,
  UploadUrlResponse,
  FileEntityResponse,
  ResourceType,
} from '@/shared/types/fileTypes';

const toFileUploadMeta = (file: File): FileUploadMeta => ({
  fileName: file.name,
  contentType: file.type || 'application/octet-stream',
  sizeBytes: file.size,
});

/** Step 1 — request signed GCS upload URLs for one or more files. */
async function requestUploadUrls(
  resourceType: ResourceType,
  resourceId: string,
  files: File[],
): Promise<UploadUrlFileResult[]> {
  const body: UploadUrlRequest = {
    resourceType,
    resourceId,
    files: files.map(toFileUploadMeta),
  };
  const response = await apiClient.post<UploadUrlResponse>(`/api/v1/files/${resourceId}/upload-url`, {
    params: { resourceType },
    body,
  });
  return response?.data?.uploads || [];
}

/**
 * Step 2 — PUT the file bytes to the signed GCS URL.
 * Uses raw fetch (not apiClient): different host, signed-URL auth, no Bearer token.
 *
 * Browser uploads require the GCS bucket CORS policy to allow this portal origin
 * for PUT + Content-Type (curl works without CORS; browsers do not).
 */
async function uploadFileToSignedUrl(slot: UploadUrlFileResult, file: File): Promise<void> {
  const contentType =
    slot.headers?.contentType || file.type || 'application/octet-stream';
  const method = (slot.method || 'PUT').toUpperCase();

  // Only send headers the signed URL expects — no Authorization / credentials.
  const headers: Record<string, string> = {
    'Content-Type': contentType,
  };

  let response: Response;
  try {
    response = await fetch(slot.uploadUrl, {
      method,
      headers,
      body: file,
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
    });
  } catch (error) {
    // Browsers surface CORS / network blocks as TypeError with an opaque message.
    throw new ApiError(
      'Upload blocked by browser CORS. The GCS images bucket must allow this portal origin for PUT requests (Content-Type). Curl works because it does not enforce CORS.',
      0,
      'GCS_CORS_BLOCKED',
      {
        error: {
          status: 0,
          code: 'GCS_CORS_BLOCKED',
          message: error instanceof Error ? error.message : 'Network / CORS error talking to Cloud Storage',
        },
      },
    );
  }

  if (!response.ok) {
    const message = `Failed to upload file to storage (${response.status})`;
    throw new ApiError(message, response.status, 'GCS_UPLOAD_FAILED');
  }
}

/** Step 3 — mark uploads complete so the API can promote pending objects. */
async function completeUpload(
  resourceType: ResourceType,
  resourceId: string,
  fileIds: string[],
): Promise<UploadCompleteResponse | APIMessageResponse> {
  const body: UploadCompleteRequest = { resourceType, resourceId, fileIds };
  return apiClient.post<UploadCompleteResponse | APIMessageResponse>(`/api/v1/files/${resourceId}/upload-complete`, {
    params: { resourceType },
    body,
  });
}

/**
 * Full 3-step upload: signed URLs → GCS PUT → upload-complete.
 * Matches files to signed slots by index (API returns one slot per requested file).
 */
async function uploadFiles({ resourceType, resourceId, files }: UploadFilesInput): Promise<string[]> {
  if (!files.length) {
    throw new ApiError('No files selected for upload', 400, 'NO_FILES');
  }

  const slots = await requestUploadUrls(resourceType, resourceId, files);
  if (slots.length !== files.length) {
    throw new ApiError(
      `Expected ${files.length} upload URL(s), received ${slots.length}`,
      500,
      'UPLOAD_URL_COUNT_MISMATCH',
    );
  }

  const fileIds: string[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const slot = slots[i];
    if (!slot?.fileId || !slot?.uploadUrl) {
      throw new ApiError(`Missing upload URL for file "${file.name}"`, 500, 'MISSING_UPLOAD_URL');
    }
    await uploadFileToSignedUrl(slot, file);
    fileIds.push(slot.fileId);
  }

  await completeUpload(resourceType, resourceId, fileIds);
  return fileIds;
}

/** React Query mutation wrapping the full upload flow. */
export const useUploadFiles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UploadFilesInput) => uploadFiles(input),
    onSuccess: (_fileIds, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['files', variables.resourceType, variables.resourceId],
      });
    },
    onError: (error: Error) => error,
  });
};

export const useGetFiles = (resourceType: ResourceType, resourceId: string) => {
  return useQuery({
    queryKey: ['files', resourceType, resourceId],
    queryFn: async () => {
      const response = await apiClient.get<FileEntityResponse>(
        `/api/v1/files/${resourceId}`,
        { params: { resourceType } });
      return response?.data?.files || [];
    },
    enabled: !!resourceType && !!resourceId
  });
};

export const useDeleteFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ resourceType, resourceId, fileId }: { resourceType: ResourceType, resourceId: string, fileId: string }) => {
      const response = await apiClient.delete<APIMessageResponse>(`/api/v1/files/${resourceId}/${fileId}`,
        { params: { resourceType } }
      );
      return response;
    },
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['files', variables.resourceType, variables.resourceId],
      });
    },
    onError: (error: Error) => error,
  });
};